import { PlayerRenderService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/player';
import type { BaseTransitionModel } from '@src/slide/domain/ui-model/element-modeles/transition/transition-model/base-transition-model';
import { IAnimationUpdateParams, ITransitionHandlerOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/slide-transition/interface';
import { Transition } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/slide-transition/transition';
import { IAnimationRenderer } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/slide-transition/render/interface';
import { createAnimationRenderer } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/slide-transition/render/factory';

export class PlayerTransitionHandler {
    private transition: Transition | null;
    private blackPlaceHolder: HTMLDivElement | null;
    private options: ITransitionHandlerOptions;
    private renderer?: IAnimationRenderer;

    constructor(options: ITransitionHandlerOptions) {
        this.options = options;
        this.renderer = createAnimationRenderer(options);
    }

    public startTransition() {
        const { slideModel, handleTimelineComplete,
            isSwitchForward, getPrevSlideModel } = this.options;
        if (!this.isSupportTransition()) {
            return;
        }

        const renderer = this.getAnimationRenderer();
        renderer?.beforeAnimation();

        const model = (isSwitchForward?.() ? getPrevSlideModel?.()?.transition
            : slideModel.transition) as BaseTransitionModel;

        this.transition = new Transition({
            model,
            isSwitchForward,
            handleTimelineComplete: () => {
                handleTimelineComplete?.();
                renderer?.afterAnimation();
            },
            handleAnimationUpdate: (params: IAnimationUpdateParams) => {
                renderer?.renderAnimationFrame(params);
            },
        });
        this.transition?.startAnimation();
    }

    public stopTransition(): boolean {
        return !!this.transition?.stopAnimation();
    }

    public isSupportTransition(): boolean {
        const { slideModel, isSwitchForward, getPrevSlideModel } = this.options;
        if (!isSwitchForward?.()) {
            return !!slideModel.transition?.isSupported;
        }
        return !!getPrevSlideModel?.()?.transition?.isSupported;
    }

    public get isTransitionActive(): boolean {
        return !!this.transition?.isActive();
    }

    public dispose() {
        this.transition?.dispose();
        this.transition = null;
    }

    public getExitElement(): HTMLElement {
        let prevCanvas;
        const prevSlideModel = this.options.getPrevSlideModel?.();
        if (prevSlideModel) {
            const prevSlideCanvasCache = PlayerRenderService.getInstance().getPrevSlideCanvas();
            // 先尝试拿缓存，没有再去根据索引获取renderTree的cache
            if (prevSlideCanvasCache) {
                prevCanvas = prevSlideCanvasCache;
            } else {
                const renderTree = PlayerRenderService.getInstance().getRenderTree(prevSlideModel);
                prevCanvas = renderTree?.getApplication()?.view?.native;
            }
        }
        if (!prevCanvas) {
            prevCanvas = this.createBlackPlaceHolder();
        }
        return prevCanvas;
    }

    private getAnimationRenderer() {
        const { isSwitchForward, getPrevSlideModel } = this.options;
        const prevSlideModel = getPrevSlideModel?.();
        if (isSwitchForward?.() && prevSlideModel) {
            const rendererOptions: ITransitionHandlerOptions = {
                ...this.options,
                slideModel: prevSlideModel,
                prevSlideModel: this.options.slideModel,
            };
            return createAnimationRenderer(rendererOptions);
        }

        return this.renderer;
    }

    private createBlackPlaceHolder(): HTMLElement {
        if (!this.blackPlaceHolder) {
            this.blackPlaceHolder = document.createElement('div');
            this.blackPlaceHolder.style.backgroundColor = '#000';
        }

        this.blackPlaceHolder.style.width = '100%';
        this.blackPlaceHolder.style.height = '100%';
        return this.blackPlaceHolder;
    }
}
